import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded'
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { getSupportFeeProofUrl } from 'src/supportPortal/services/supportBookedEventActionsService'

dayjs.extend(utc)

const PAYMENT_METHOD_LABELS = {
  paymentLink: 'Payment Link',
  paymentLinkInitiatedByMentor: 'Payment Link',
  paymentLinkOnScreen: 'Payment Link (on screen)',
  razorpay: 'Razorpay',
  supportConfirmed: 'Support Confirmed',
  paidOffline: 'Paid Offline',
  credits: 'Credits',
  cash: 'Cash',
}

/** Resolves the settlement party for display: explicit field, else derived from feePaymentMode. */
function resolveSettlementParty(bookedEvent, feeDetails) {
  const explicit = feeDetails?.settlementParty
  if (explicit === 'aumhum' || explicit === 'clinic') {
    return explicit
  }
  if (bookedEvent?.feePaymentMode) {
    return bookedEvent.feePaymentMode === 'aumhum' ? 'aumhum' : 'clinic'
  }
  return null
}

function isImageMime(mimeType) {
  return typeof mimeType === 'string' && mimeType.startsWith('image/')
}

function FeeProofRow({ document }) {
  const [opening, setOpening] = useState(false)

  async function handleOpen() {
    if (opening) {
      return
    }
    setOpening(true)
    const result = await getSupportFeeProofUrl(document.fileId)
    setOpening(false)
    if (result.ok && result.url) {
      window.open(result.url, '_blank', 'noopener')
    } else {
      toast.error(result.message || 'Could not open the document')
    }
  }

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      onClick={handleOpen}
      sx={{
        px: 1,
        py: 0.75,
        borderRadius: '8px',
        border: '1px solid rgba(226, 232, 240, 0.9)',
        cursor: 'pointer',
        '&:hover': { backgroundColor: '#f8fafc', borderColor: 'rgba(148, 163, 184, 0.6)' },
      }}
    >
      {isImageMime(document.mimeType) ? (
        <ImageOutlinedIcon sx={{ fontSize: 18, color: '#4f46e5' }} />
      ) : (
        <InsertDriveFileOutlinedIcon sx={{ fontSize: 18, color: '#64748b' }} />
      )}
      <Typography
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: 12.5,
          fontWeight: 600,
          color: '#334155',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {document.fileName || 'Document'}
      </Typography>
      {opening ? (
        <CircularProgress size={14} />
      ) : (
        <OpenInNewRoundedIcon sx={{ fontSize: 15, color: '#94a3b8' }} />
      )}
    </Stack>
  )
}

function Row({ label, value, valueColor = '#0f172a' }) {
  if (value === null || value === undefined || value === '') {
    return null
  }
  return (
    <Stack direction="row" spacing={1.5} sx={{ py: 0.45 }}>
      <Typography
        sx={{
          fontSize: 12,
          color: '#64748b',
          minWidth: 150,
          flexShrink: 0,
          textTransform: 'uppercase',
          fontWeight: 700,
          letterSpacing: 0.3,
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: 13, color: valueColor, fontWeight: 600, wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Stack>
  )
}

function formatInstant(value) {
  if (!value) {
    return null
  }
  return dayjs.utc(value).local().format('DD MMM YYYY, h:mm A')
}

/**
 * CC-parity "Fee Collection Details" dialog: amount, payment status/method, collection
 * metadata, order/txn references and the fee-collection note (copyable).
 */
export default function SupportFeeDetailsDialog({ open, onClose, bookedEvent }) {
  const feeDetails = bookedEvent?.feeCollectionDetails || {}
  const adminConfirmPayment = bookedEvent?.adminConfirmPayment
  const note = feeDetails?.notes || bookedEvent?.feeCollectionNote
  const paymentMethodRaw =
    feeDetails?.paymentGateway || bookedEvent?.paymentGateway || bookedEvent?.paymentMethod
  const paymentMethodLabel = PAYMENT_METHOD_LABELS[paymentMethodRaw] || paymentMethodRaw
  const amountLabel =
    bookedEvent?.feePaid !== undefined && bookedEvent?.feePaid !== null
      ? `${bookedEvent?.currency || 'INR'} ${bookedEvent.feePaid}`
      : null
  const settlementParty = resolveSettlementParty(bookedEvent, feeDetails)
  const isAumhumCollected = settlementParty === 'aumhum'
  const proofDocuments = (feeDetails?.proofDocuments || []).filter((doc) => doc?.fileId)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <ReceiptLongRoundedIcon sx={{ color: '#4f46e5' }} />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 17, color: '#0f172a' }}>
              Fee Collection Details
            </Typography>
            {amountLabel ? (
              <Typography sx={{ fontSize: 12.5, color: '#64748b' }}>{amountLabel}</Typography>
            ) : null}
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          {settlementParty ? (
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="center"
              sx={{
                p: 1.25,
                borderRadius: 2,
                border: `1px solid ${isAumhumCollected ? 'rgba(79, 70, 229, 0.28)' : 'rgba(5, 150, 105, 0.28)'}`,
                backgroundColor: isAumhumCollected ? 'rgba(79, 70, 229, 0.06)' : 'rgba(5, 150, 105, 0.06)',
              }}
            >
              {isAumhumCollected ? (
                <VerifiedRoundedIcon sx={{ color: '#4f46e5', fontSize: 22 }} />
              ) : (
                <ApartmentRoundedIcon sx={{ color: '#047857', fontSize: 22 }} />
              )}
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 10.5,
                    fontWeight: 800,
                    letterSpacing: 0.4,
                    textTransform: 'uppercase',
                    color: '#94a3b8',
                    lineHeight: 1.2,
                  }}
                >
                  Settlement
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 800,
                    color: isAumhumCollected ? '#4338ca' : '#047857',
                    lineHeight: 1.25,
                  }}
                >
                  Collected by {isAumhumCollected ? 'AUMHUM' : 'Clinic'}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: '#64748b', lineHeight: 1.3 }}>
                  {isAumhumCollected
                    ? 'Settles as AUMHUM-collected in the payout report.'
                    : 'Settles as clinic-collected in the payout report.'}
                </Typography>
              </Box>
            </Stack>
          ) : null}

          {note ? (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                backgroundColor: 'rgba(254, 249, 195, 0.6)',
                border: '1px solid rgba(234, 179, 8, 0.35)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: '#a16207',
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                  }}
                >
                  Fee Collection Note
                </Typography>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#422006', mt: 0.25 }}>
                  {note}
                </Typography>
              </Box>
              <Tooltip title="Copy note">
                <IconButton
                  size="small"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(note)
                      toast.success('Note copied')
                    } catch (error) {
                      toast.error('Could not copy the note')
                    }
                  }}
                >
                  <ContentCopyRoundedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          ) : null}

          <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
            {paymentMethodLabel ? (
              <Chip label={paymentMethodLabel} size="small" sx={{ fontWeight: 700 }} />
            ) : null}
            {bookedEvent?.paymentStatus ? (
              <Chip
                label={bookedEvent.paymentStatus}
                size="small"
                variant="outlined"
                color={bookedEvent.paymentStatus === 'paid' ? 'success' : 'warning'}
              />
            ) : null}
            {bookedEvent?.testEvent ? (
              <Chip label="test booking" size="small" variant="outlined" color="warning" />
            ) : null}
          </Stack>

          <Divider />

          <Box>
            <Row label="Amount" value={amountLabel} />
            <Row label="Payment status" value={bookedEvent?.paymentStatus} />
            <Row label="Payment method" value={paymentMethodLabel} />
            <Row label="Collected by" value={feeDetails?.collectedBy} />
            <Row label="Order id" value={feeDetails?.orderId} />
            <Row
              label="Actual fee collected"
              value={
                feeDetails?.actualFeeCollected !== undefined && feeDetails?.actualFeeCollected !== null
                  ? `${feeDetails.actualFeeCollected}`
                  : null
              }
            />
            <Row label="Collected on" value={formatInstant(feeDetails?.collectedOn)} />
            <Row label="Booked on" value={formatInstant(bookedEvent?.createdTime)} />
          </Box>

          {adminConfirmPayment ? (
            <>
              <Divider />
              <Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: '#047857',
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                    mb: 0.5,
                  }}
                >
                  Support Payment Confirmation
                </Typography>
                <Row label="Reason" value={adminConfirmPayment.reasonGiven} valueColor="#047857" />
                <Row label="Mode used" value={adminConfirmPayment.paymentGatewayUsed} />
                <Row label="Txn reference" value={adminConfirmPayment.paymentTxnId} />
              </Box>
            </>
          ) : null}

          {proofDocuments.length > 0 ? (
            <>
              <Divider />
              <Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: '#475569',
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                    mb: 0.75,
                  }}
                >
                  Proof documents
                </Typography>
                <Stack spacing={0.75}>
                  {proofDocuments.map((document) => (
                    <FeeProofRow key={document.fileId} document={document} />
                  ))}
                </Stack>
              </Box>
            </>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
